import { CommonModule, DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { AngularMaterialCommonModule } from '@app/shared/angular-material/angular-material-common.module';
import { PlanDataEnum } from '@app/plan/plan.model';
import { PlanService } from '@app/plan/plan.service';

import { SearchResultsComponent } from '../search-results/search-results.component';
import { SearchService } from '../search.service';

@Component({
  selector: 'app-desktop-search-container',
  templateUrl: './desktop-search-container.component.html',
  styleUrl: './desktop-search-container.component.scss',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AngularMaterialCommonModule,
    SearchResultsComponent
  ]
})
export class DesktopSearchContainerComponent implements OnInit {
  _window = this.document.defaultView;
  windowScrollListener: EventListener;
  outsideClickListener: EventListener;
  escapeKeyUpListener: EventListener;

  @ViewChild('container')
  containerEl: ElementRef<HTMLDivElement>;
  @ViewChild('searchInput')
  searchInputEl: ElementRef<HTMLInputElement>;

  constructor(
    private searchService: SearchService,
    private planService: PlanService,
    @Inject(DOCUMENT) private document: Document,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.isOpened$.subscribe((isOpened) => {
      if (isOpened) {
        this.handleOpen();
      } else {
        this.handleClose();
      }
    });
  }

  get isOpened$() {
    return this.searchService.isDesktopSearchContainerOpened.asObservable();
  }

  get searchForm() {
    return this.searchService.searchForm;
  }

  watchUserInteractions() {
    this.windowScrollListener = () => {
      this.handleClose();
    };

    this.outsideClickListener = (e) => {
      const element = this.elementRef.nativeElement;
      const isClickOutsideContainer = !element.contains(
        e.target as HTMLElement
      );
      if (isClickOutsideContainer) {
        this.handleClose();
      }
    };

    this.escapeKeyUpListener = (e: KeyboardEvent) => {
      if (e.code === 'Escape') {
        this.handleClose();
      }
    };

    setTimeout(() => {
      this._window.addEventListener('scroll', this.windowScrollListener);
      this.document.addEventListener('click', this.outsideClickListener);
      this.document.addEventListener('keyup', this.escapeKeyUpListener);
    }, 0);
  }

  removeListeners() {
    if (this.windowScrollListener) {
      this._window.removeEventListener('scroll', this.windowScrollListener);
    }
    if (this.outsideClickListener) {
      this.document.removeEventListener('click', this.outsideClickListener);
    }
    if (this.escapeKeyUpListener) {
      this.document.removeEventListener('keyup', this.escapeKeyUpListener);
    }
  }

  handleOpen() {
    const registration = this.planService.getPlanData(
      PlanDataEnum.Registration
    );
    this.searchService.init(registration);

    const containerEl = this.containerEl.nativeElement;
    containerEl.style.display = 'block';
    containerEl.animate([{ opacity: 1 }], {
      duration: 250,
      easing: 'ease-out',
      fill: 'forwards'
    });

    const searchInputEl = this.searchInputEl.nativeElement;
    searchInputEl.focus();

    this.watchUserInteractions();
  }

  handleClose() {
    if (!this.containerEl) return;

    const containerEl = this.containerEl.nativeElement;
    containerEl.animate([{ opacity: 0 }], {
      duration: 250,
      easing: 'ease-out',
      fill: 'forwards'
    }).onfinish = () => {
      containerEl.style.display = 'none';
      this.removeListeners();

      const isDesktopSearchContainerOpened =
        this.searchService.isDesktopSearchContainerOpened.value;
      if (isDesktopSearchContainerOpened) {
        this.searchService.isDesktopSearchContainerOpened.next(false);
      }
    };
  }
}
